import type { ReactNode } from "react";
import { DAY_MS } from "@amber/contracts";
import { AppLink, Callout, DrawerSection, formatDateTime } from "@amber/ui";
import { QUOTA_GROUPS, QUOTA_GROUP_LABELS, TIER_LABELS, type SourceDetail } from "../../contracts/index";
import { CoverageTimeline, HealthLabel, Monogram, TimelineAxis, formatDays, nextScanText } from "./SourceVisuals";
import { GapMenu, gapInterval } from "./GapActions";

const isQuotaGroup = (q: string): q is (typeof QUOTA_GROUPS)[number] => QUOTA_GROUPS.some((g) => g === q);

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted">{label}</dt>
      <dd className="m-0 text-fg">{children}</dd>
    </>
  );
}

function personPlanText(p: { pending: boolean; in_plan: boolean }): string {
  if (p.pending) return "Changes pending";
  return p.in_plan ? "In their plan" : "Not in their plan";
}

export function SourceSummary({ detail }: { detail: SourceDetail }) {
  const s = detail.source;
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <HealthLabel state={s.health.state} label={s.health.label} />
        {s.next_scan.kind === "scanning" ? (
          <span className="rounded-full bg-accent-soft px-2 text-xs leading-5 text-link">Scanning now</span>
        ) : null}
        {s.required ? (
          <span className="rounded-full border border-divider px-2 text-xs leading-5 text-muted">Required route</span>
        ) : null}
      </div>
      <p className="m-0 break-all text-md text-muted">{detail.canonical_url}</p>
      {s.people.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {s.people.map((p) => (
            <li key={p.id} className="flex items-center gap-2">
              <Monogram name={p.name} />
              <span className="text-fg">{p.name}</span>
              <span className="text-md text-muted">
                {p.relationship
                  ? `${p.relationship[0]?.toUpperCase() ?? ""}${p.relationship.slice(1).replace("_", " ")} · `
                  : ""}
                {personPlanText(p)}
                {p.required ? " · Required route" : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {s.people.length > 1 ? (
        <p className="m-0 text-md text-muted">Shared source: {s.people.length} people depend on it.</p>
      ) : null}
      {s.shared_plan ? (
        <p className="m-0 text-md text-muted">Shared-source plan{s.shared_plan.pending ? " · Changes pending" : ""}</p>
      ) : null}
      <HealthCallout detail={detail} />
    </>
  );
}

function healthTone(state: SourceDetail["source"]["health"]["state"]): "attention" | "waiting" | "neutral" {
  if (state === "failing" || state === "partial") return "attention";
  return state === "late" ? "waiting" : "neutral";
}

function HealthCallout({ detail }: { detail: SourceDetail }) {
  const s = detail.source;
  if (s.health.state === "not_in_any_plan") {
    return (
      <Callout tone="neutral">
        Not in any plan, so it isn&apos;t scanned. Add it to a person&apos;s plan in Profiles, or monitor it as a shared
        source.
      </Callout>
    );
  }
  if (!s.health.reason) return null;
  return (
    <Callout tone={healthTone(s.health.state)}>
      {s.health.reason}
      {s.health.state === "unusually_quiet" && s.usual_gap_days !== null ? (
        <span className="block text-md">
          Usual gap {formatDays(s.usual_gap_days)}, the median of the last 10 publications.
        </span>
      ) : null}
      <span className="block text-md">Next: {nextScanText(s)}</span>
    </Callout>
  );
}

export function CoverageSection({ detail, onGaps }: { detail: SourceDetail; onGaps: () => void }) {
  const s = detail.source;
  const to = new Date().toISOString();
  const from = new Date(Date.now() - 90 * DAY_MS).toISOString();
  return (
    <DrawerSection title="Coverage · last 90 days">
      <CoverageTimeline row={s} from={from} to={to} large onGap={onGaps} />
      <TimelineAxis from={from} to={to} />
      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-md">
        <Fact label="Complete through">
          {s.complete_through ? formatDateTime(s.complete_through) : "Not complete yet"}
        </Fact>
        {s.complete_back_to ? (
          <Fact label="Complete back to">
            {formatDateTime(s.complete_back_to)} ·{" "}
            <AppLink href="/settings/history" className="text-link underline">
              History window
            </AppLink>
          </Fact>
        ) : null}
        {s.backfill ? (
          <Fact
            label={s.backfill.kind === "catch_up" ? "Catch-up" : "Backfill"}
          >{`${String(s.backfill.days_read)} of ${String(s.backfill.days_total)} days read`}</Fact>
        ) : null}
        <Fact label="Last scan">
          {s.last_scan ? `${formatDateTime(s.last_scan.at)} · ${s.last_scan.result}` : "None yet"}
        </Fact>
        {s.pause ? (
          <Fact label="Paused since">{`${formatDateTime(s.pause.at)} by ${s.pause.by}${s.pause.person ? ` (pause of ${s.pause.person})` : ""}: ${s.pause.reason}`}</Fact>
        ) : null}
        {detail.monitored_since ? <Fact label="Monitored since">{formatDateTime(detail.monitored_since)}</Fact> : null}
        {detail.supplier ? (
          <>
            <Fact label="Supplier">{detail.supplier.name}</Fact>
            <Fact label="Supplier coverage">
              {detail.supplier.coverage}
              <span className="block text-muted">{`${detail.supplier.limits} · recorded ${formatDateTime(detail.supplier.recorded_at)}`}</span>
            </Fact>
          </>
        ) : null}
      </dl>
      {detail.gaps.length > 0 ? (
        <ul className="mt-3 flex list-none flex-col gap-2 p-0">
          {detail.gaps.map((g) => (
            <li key={g.id} className="rounded-lg border border-divider px-3 py-2 text-md">
              <span className="block font-strong text-fg">{gapInterval(g)}</span>
              <span className="block text-muted">{g.reason}</span>
              <span className="block text-muted">
                {g.resolved
                  ? `${g.resolved.how} · ${formatDateTime(g.resolved.at)}${g.resolved.note ? ` · “${g.resolved.note}”` : ""}`
                  : `${g.next_step} · open since ${formatDateTime(g.opened_at)}`}
              </span>
              {g.resolved ? null : (
                <span className="mt-1.5 block">
                  <GapMenu gap={g} actions={detail.actions} />
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </DrawerSection>
  );
}

const YEAR_CLASS: Partial<Record<string, string>> = {
  partial: "font-strong text-waiting",
  not_collected: "font-strong text-muted",
};

export function YearsSection({ detail }: { detail: SourceDetail }) {
  return detail.years.length > 0 ? (
    <DrawerSection title="Coverage by year" note="within the history window">
      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-md">
        {detail.years.map((y) => (
          <Fact key={y.year} label={String(y.year)}>
            <span className={YEAR_CLASS[y.state] ?? "font-strong text-fg"}>{y.label}</span>
            <span className="text-muted">{` · ${y.detail}`}</span>
          </Fact>
        ))}
      </dl>
    </DrawerSection>
  ) : null;
}

export function ScansSection({ detail }: { detail: SourceDetail }) {
  return (
    <DrawerSection title="Recent scans">
      {detail.scans.length === 0 ? (
        <p className="m-0 text-md text-muted">No scans yet.</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-md">
          {detail.scans.map((scan) => (
            <li key={scan.id}>
              <span className="text-fg">
                {formatDateTime(scan.at)} · <b className="font-strong">{scan.result}</b>
              </span>{" "}
              <span className="text-muted">
                · {scan.kind} · {scan.items_new} new of {scan.items_found}
                {scan.reach ? ` · ${scan.reach}` : ""}
              </span>
              {scan.reason ? <span className="block text-muted">{scan.reason}</span> : null}
              {scan.engine_url ? (
                <a href={scan.engine_url} target="_blank" rel="noreferrer noopener" className="text-link">
                  Open engine run ↗
                </a>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </DrawerSection>
  );
}

export function SettingsSection({ detail }: { detail: SourceDetail }) {
  const s = detail.source;
  return (
    <DrawerSection title="Settings">
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-md">
        <Fact label="Cadence">{s.tier_label}</Fact>
        <Fact label="Plans">
          {detail.plans.length === 0
            ? "None"
            : detail.plans
                .map(
                  (p) =>
                    `${p.owner} (${TIER_LABELS[p.tier]}${p.required ? ", required" : ""}${p.pending ? ", changes pending" : ""})`,
                )
                .join("; ")}
        </Fact>
        <Fact label="Known breaks">
          {detail.breaks.length === 0
            ? "None"
            : detail.breaks
                .map((b) => `${b.label} (${b.from} – ${b.to ?? "until further notice"}${b.ended_at ? ", ended" : ""})`)
                .join("; ")}
        </Fact>
        <Fact label="Quota group">
          {detail.quota_group && isQuotaGroup(detail.quota_group)
            ? QUOTA_GROUP_LABELS[detail.quota_group]
            : "Platform default"}
        </Fact>
        <Fact label="Owner team">{detail.owner_team ?? "None"}</Fact>
        <Fact label="Items (7 days)">{s.items_7d}</Fact>
        <Fact label="Download problems">
          {s.download_problems > 0 ? `${String(s.download_problems)} · doesn't affect health` : "None"}
        </Fact>
      </dl>
    </DrawerSection>
  );
}

export function HistorySection({ detail }: { detail: SourceDetail }) {
  return (
    <DrawerSection title="History">
      {detail.history.length === 0 ? (
        <p className="m-0 text-md text-muted">No changes recorded.</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1 p-0 text-md">
          {detail.history.map((h) => (
            <li key={`${h.at}-${h.action}`}>
              <span className="text-fg">{h.action.replace("collection.", "").replace(/_/g, " ")}</span>{" "}
              <span className="text-muted">
                · {h.actor} · {formatDateTime(h.at)}
              </span>
              {h.reason ? <span className="block text-muted">“{h.reason}”</span> : null}
              {h.changes.map((c) => (
                <span key={c.label} className="block text-muted">
                  {c.label}: <del>{c.before}</del> → <span className="text-fg">{c.after}</span>
                </span>
              ))}
            </li>
          ))}
        </ul>
      )}
      <AppLink href={`/settings/audit?subject=${detail.source.id}`} className="mt-1 inline-block text-md text-link">
        Open in audit log
      </AppLink>
    </DrawerSection>
  );
}
